import Big from "big.js";
import { t } from "i18next";
import { Dispatch, useState } from "react";
import { KeyedMutator, mutate } from "swr";

import ArrowUpRight from "@/assets/icons/arrow-up-right.svg?react";
import Close from "@/assets/icons/close.svg?react";
import Mark from "@/assets/icons/mark.svg?react";
import Hapi from "@/assets/images/hapi.svg?react";
import { Button } from "@/components/Button";
import { useAirdrops } from "@/hooks/store";
import { useNetworkDependentActions } from "@/hooks/useNetworkDependentActions";
import { Network } from "@/interfaces/network";
import { AirdropNFTProps, AirdropNFTStatus } from "@/interfaces/nft";
import { AirdropProps } from "@/interfaces/store";
import { AirdropResponse } from "@/services/rpc/near";
import { displayAmount, formatMonetaryAmount, formatTokenAmount } from "@/utils";

import styles from "./styles";

const HeaderStatus = ({ status }: { status: AirdropNFTStatus }) => {
  switch (status) {
    case "fulfilled":
      return (
        <styles.NFT.Circle $status={status}>
          <Mark />
        </styles.NFT.Circle>
      );
    case "rejected":
      return (
        <styles.NFT.Circle $status={status}>
          <Close />
        </styles.NFT.Circle>
      );
    default:
      null;
  }
};

const FooterStatus = ({
  status,
  balanceCapacity,
  currentLimit,
  claimByPerson,
  tokenSymbol,
}: {
  status: AirdropNFTStatus;
  balanceCapacity: string;
  currentLimit: string;
  claimByPerson: string;
  tokenSymbol: string;
}) => {
  switch (status) {
    case "fulfilled":
      return null;
    case "rejected": {
      return (
        <styles.NFT.Holder>
          <Hapi />
          <div>{t("Airdrop.HAPILimit", { balance: balanceCapacity, limit: currentLimit })}</div>
        </styles.NFT.Holder>
      );
    }
    default:
      return (
        <styles.NFT.ClaimedByUser>
          <Mark />
          {t("Airdrop.ClaimedByUser", { amount: formatMonetaryAmount(claimByPerson), symbol: tokenSymbol })}
        </styles.NFT.ClaimedByUser>
      );
  }
};

const calculateFooterReminder = (
  oneRewardPerClaim: boolean,
  currentNftId: number,
  requiredTokenBalance?: string,
  tokenBalance?: string | null,
  claimedCount = "0",
) => {
  if (!oneRewardPerClaim && tokenBalance) {
    return formatTokenAmount(tokenBalance);
  } else if (tokenBalance) {
    if (!requiredTokenBalance || requiredTokenBalance === "0") return "0";
    const divisionReminder = Big(tokenBalance)
      .minus(claimedCount)
      .mod(requiredTokenBalance ?? "0");

    const nftCapacity = Big(tokenBalance)
      .minus(claimedCount)
      .div(requiredTokenBalance ?? "1")
      .round(0, 0)
      .toFixed();

    return Number(nftCapacity) === currentNftId - 1 ? displayAmount(divisionReminder.toFixed(), 18) : "0";
  }

  return "0";
};

export const NFT = (
  nft: AirdropNFTProps & {
    currentNftId: number;
    claimByPerson: string;
    tokenSymbol: string;
    claiming: boolean;
    fulfilledNFTs: AirdropNFTProps[];
    availableNFTsToClaim: number;
    setClaiming: Dispatch<boolean>;
    claimedCount?: string;
    airdrop: AirdropProps & { mutateAirdrops: KeyedMutator<AirdropResponse[] | undefined> };
  },
) => {
  const { claimAirdrop } = useNetworkDependentActions();
  const [localLoading, setLocalLoading] = useState(false);
  const hapiBalance = useAirdrops((store) => store.hapiBalance);
  const isButtonDisabled =
    nft.airdrop.status !== "active" ||
    Big(nft.airdrop.claimed_amount).gte(nft.airdrop.total_amount) ||
    nft.airdrop.is_stopped ||
    !nft.fulfilledNFTs.length;

  const claim = async () => {
    setLocalLoading(true);
    try {
      if (!nft.fulfilledNFTs.length) return;
      nft.setClaiming(true);
      await claimAirdrop(nft.airdrop.id, [nft.nftId]);
      mutate(`AIRDROPS/${Network.NEAR}`);
      nft.setClaiming(false);
    } finally {
      setLocalLoading(false);
    }
  };

  const buyHapi = async () => {
    window.parent.postMessage(
      {
        navigate: "/swaps?to=1010:d9c2d319cd7e6177336b0a9c93c21cb48d84fb54.factory.bridge.near",
      },
      "*",
    );
  };

  const balanceCapacity = calculateFooterReminder(
    nft.airdrop.one_reward_per_claim,
    nft.currentNftId,
    nft.airdrop.required_token_balance,
    hapiBalance,
    nft.claimedCount,
  );

  const showButton = nft.status !== "claimed";
  const showBuyButton =
    nft.status === "rejected" && balanceCapacity < formatTokenAmount(nft.airdrop.required_token_balance);

  return (
    <styles.NFT.Component key={nft.nftId}>
      <img src={nft.url} alt={nft.nftId} />
      <div>
        <styles.NFT.Text>
          #{nft.nftId} <HeaderStatus status={nft.status} />
        </styles.NFT.Text>
        <FooterStatus
          balanceCapacity={balanceCapacity}
          currentLimit={formatTokenAmount(nft.airdrop.required_token_balance)}
          status={nft.status}
          claimByPerson={nft.claimByPerson}
          tokenSymbol={nft.tokenSymbol}
        />
      </div>

      {showButton ? (
        showBuyButton ? (
          <styles.BuyHapi onClick={buyHapi}>
            <span>{t("Actions.Buy")}</span>
            <ArrowUpRight />
          </styles.BuyHapi>
        ) : (
          <Button name={"Claim"} handler={claim} loading={localLoading} disabled={isButtonDisabled || nft.claiming} />
        )
      ) : null}
    </styles.NFT.Component>
  );
};
